import {
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
  } from '@angular/core';
  import { Subscription } from 'rxjs';
import { Permission } from './permissions';
import { AccessService } from './access.service';
  
  
  export type PermissionCheckMode = 'all' | 'any';
  
  @Directive({
    selector: '[appPermissionDisabled]',
  })
  export class PermissionDisabledDirective
    implements OnInit, OnDestroy {
  
    private permissions: readonly Permission[] = [];
    private mode: PermissionCheckMode = 'all';
    private disableInPreviewMode = false;
  
    private disabled = true;
  
    private readonly originalTabIndex: string | null;
    private readonly accessSubscription: Subscription;
  
    private readonly blockedEventHandler = (
      event: Event,
    ): void => {
      if (!this.disabled) {
        return;
      }
  
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
  
    private readonly blockedKeyboardHandler = (
      event: KeyboardEvent,
    ): void => {
      if (
        !this.disabled ||
        (event.key !== 'Enter' && event.key !== ' ')
      ) {
        return;
      }
  
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
  
    constructor(
      private readonly elementRef:
        ElementRef<HTMLElement>,
      private readonly renderer: Renderer2,
      private readonly accessService: AccessService,
    ) {
      this.originalTabIndex =
        this.elementRef.nativeElement.getAttribute(
          'tabindex',
        );
  
      this.accessSubscription =
        this.accessService.access$.subscribe(() => {
          this.updateState();
        });
    }
  
    @Input()
    set appPermissionDisabled(
      value: Permission | readonly Permission[],
    ) {
      this.permissions =
        typeof value === 'string'
          ? [value]
          : value ?? [];
  
      this.updateState();
    }
  
    @Input()
    set appPermissionDisabledMode(
      value: PermissionCheckMode,
    ) {
      this.mode = value ?? 'all';
      this.updateState();
    }
  
    @Input()
    set appPermissionDisabledInPreview(
      value: boolean,
    ) {
      this.disableInPreviewMode = value;
      this.updateState();
    }
  
    ngOnInit(): void {
      const element = this.elementRef.nativeElement;
  
      /*
       * Capture phase prevents events before they reach
       * click handlers attached to this element or its children.
       */
      element.addEventListener(
        'pointerdown',
        this.blockedEventHandler,
        true,
      );
  
      element.addEventListener(
        'click',
        this.blockedEventHandler,
        true,
      );
  
      element.addEventListener(
        'keydown',
        this.blockedKeyboardHandler,
        true,
      );
  
      this.updateState();
    }
  
    ngOnDestroy(): void {
      const element = this.elementRef.nativeElement;
  
      element.removeEventListener(
        'pointerdown',
        this.blockedEventHandler,
        true,
      );
  
      element.removeEventListener(
        'click',
        this.blockedEventHandler,
        true,
      );
  
      element.removeEventListener(
        'keydown',
        this.blockedKeyboardHandler,
        true,
      );
  
      this.accessSubscription.unsubscribe();
    }
  
    private updateState(): void {
      const hasPermission =
        this.permissions.length === 0 ||
        (
          this.mode === 'any'
            ? this.accessService.hasAny(
                this.permissions,
              )
            : this.accessService.hasAll(
                this.permissions,
              )
        );
  
      const blockedByPreview =
        this.disableInPreviewMode &&
        this.accessService.isPreviewMode;
  
      this.setDisabled(
        !hasPermission || blockedByPreview,
      );
    }
  
    private setDisabled(disabled: boolean): void {
      this.disabled = disabled;
  
      const element = this.elementRef.nativeElement;
  
      this.renderer.setAttribute(
        element,
        'aria-disabled',
        disabled.toString(),
      );
  
      if (disabled) {
        this.renderer.addClass(
          element,
          'permission-disabled',
        );
  
        this.renderer.setAttribute(
          element,
          'tabindex',
          '-1',
        );
      } else {
        this.renderer.removeClass(
          element,
          'permission-disabled',
        );
  
        this.restoreTabIndex();
      }
  
      if (this.supportsNativeDisabled(element)) {
        this.renderer.setProperty(
          element,
          'disabled',
          disabled,
        );
      }
    }
  
    private restoreTabIndex(): void {
      const element = this.elementRef.nativeElement;
  
      if (this.originalTabIndex === null) {
        this.renderer.removeAttribute(
          element,
          'tabindex',
        );
  
        return;
      }
  
      this.renderer.setAttribute(
        element,
        'tabindex',
        this.originalTabIndex,
      );
    }
  
    private supportsNativeDisabled(
      element: HTMLElement,
    ): boolean {
      return (
        element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLFieldSetElement ||
        element instanceof HTMLOptGroupElement ||
        element instanceof HTMLOptionElement
      );
    }
  }